<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  room: { type: Object, default: () => ({}) },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'close']);
const { t } = useI18n();

const name = ref(props.room.name || '');
const description = ref(props.room.description || '');
const amenities = ref(props.room.amenities || '');
const maxGuests = ref(props.room.max_guests || 2);
const freeAgeLimit = ref(props.room.free_age_limit || 0);
const totalUnits = ref(props.room.total_units || 1);
const active = ref(props.room.active !== undefined ? props.room.active : true);

// Adult pricing
const price1Night = ref(props.room.price_1_night || '');
const price2Nights = ref(props.room.price_2_nights || '');
const price3Nights = ref(props.room.price_3_nights || '');
const holidayPrice1Night = ref(props.room.holiday_price_1_night || '');
const holidayPrice2Nights = ref(props.room.holiday_price_2_nights || '');
const holidayPrice3Nights = ref(props.room.holiday_price_3_nights || '');

// Child 1 (younger)
const child1MinAge = ref(props.room.child1_min_age ?? 2);
const child1MaxAge = ref(props.room.child1_max_age ?? 5);
const child1Price1 = ref(props.room.child1_price_1_night || '');
const child1Price2 = ref(props.room.child1_price_2_nights || '');
const child1Price3 = ref(props.room.child1_price_3_nights || '');
const holidayChild1Price1 = ref(props.room.holiday_child1_price_1_night || '');
const holidayChild1Price2 = ref(props.room.holiday_child1_price_2_nights || '');
const holidayChild1Price3 = ref(props.room.holiday_child1_price_3_nights || '');

// Child 2 (older)
const child2MinAge = ref(props.room.child2_min_age ?? 6);
const child2MaxAge = ref(props.room.child2_max_age ?? 12);
const child2Price1 = ref(props.room.child2_price_1_night || '');
const child2Price2 = ref(props.room.child2_price_2_nights || '');
const child2Price3 = ref(props.room.child2_price_3_nights || '');
const holidayChild2Price1 = ref(props.room.holiday_child2_price_1_night || '');
const holidayChild2Price2 = ref(props.room.holiday_child2_price_2_nights || '');
const holidayChild2Price3 = ref(props.room.holiday_child2_price_3_nights || '');

// Availability
const availableDates = ref([...(props.room.available_dates || [])]);
const now = new Date();
const calendarYear = ref(now.getFullYear());
const calendarMonth = ref(now.getMonth());

const calendarTitle = computed(() => {
  return new Date(calendarYear.value, calendarMonth.value, 1)
    .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
});
const calendarDays = computed(() => {
  const y = calendarYear.value, m = calendarMonth.value;
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    days.push({ day: d, date: ds, selected: availableDates.value.includes(ds), past: new Date(ds) < new Date(new Date().toISOString().split('T')[0]) });
  }
  return days;
});
const prevMonth = () => { if (calendarMonth.value === 0) { calendarMonth.value = 11; calendarYear.value--; } else calendarMonth.value--; };
const nextMonth = () => { if (calendarMonth.value === 11) { calendarMonth.value = 0; calendarYear.value++; } else calendarMonth.value++; };
const toggleDate = ds => { const i = availableDates.value.indexOf(ds); if (i >= 0) availableDates.value.splice(i, 1); else availableDates.value.push(ds); };
const selectAllMonth = () => {
  const y = calendarYear.value, m = calendarMonth.value, dim = new Date(y, m + 1, 0).getDate(), today = new Date().toISOString().split('T')[0];
  for (let d = 1; d <= dim; d++) { const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; if (ds >= today && !availableDates.value.includes(ds)) availableDates.value.push(ds); }
};
const clearAllMonth = () => { const p = `${calendarYear.value}-${String(calendarMonth.value + 1).padStart(2, '0')}-`; availableDates.value = availableDates.value.filter(d => !d.startsWith(p)); };
const selectedCountThisMonth = computed(() => { const p = `${calendarYear.value}-${String(calendarMonth.value + 1).padStart(2, '0')}-`; return availableDates.value.filter(d => d.startsWith(p)).length; });
const totalSelectedDates = computed(() => availableDates.value.length);
const isFormValid = computed(() => name.value.trim().length > 0 && maxGuests.value > 0 && totalUnits.value > 0);

const handleSubmit = () => {
  if (!isFormValid.value) return;
  emit('submit', {
    name: name.value.trim(), description: description.value, amenities: amenities.value,
    max_guests: Number(maxGuests.value), free_age_limit: Number(freeAgeLimit.value), total_units: Number(totalUnits.value),
    price_1_night: Number(price1Night.value) || 0, price_2_nights: Number(price2Nights.value) || 0, price_3_nights: Number(price3Nights.value) || 0,
    holiday_price_1_night: Number(holidayPrice1Night.value) || 0, holiday_price_2_nights: Number(holidayPrice2Nights.value) || 0, holiday_price_3_nights: Number(holidayPrice3Nights.value) || 0,
    child1_min_age: Number(child1MinAge.value), child1_max_age: Number(child1MaxAge.value),
    child1_price_1_night: Number(child1Price1.value) || 0, child1_price_2_nights: Number(child1Price2.value) || 0, child1_price_3_nights: Number(child1Price3.value) || 0,
    holiday_child1_price_1_night: Number(holidayChild1Price1.value) || 0, holiday_child1_price_2_nights: Number(holidayChild1Price2.value) || 0, holiday_child1_price_3_nights: Number(holidayChild1Price3.value) || 0,
    child2_min_age: Number(child2MinAge.value), child2_max_age: Number(child2MaxAge.value),
    child2_price_1_night: Number(child2Price1.value) || 0, child2_price_2_nights: Number(child2Price2.value) || 0, child2_price_3_nights: Number(child2Price3.value) || 0,
    holiday_child2_price_1_night: Number(holidayChild2Price1.value) || 0, holiday_child2_price_2_nights: Number(holidayChild2Price2.value) || 0, holiday_child2_price_3_nights: Number(holidayChild2Price3.value) || 0,
    active: active.value, available_dates: availableDates.value.sort(),
  });
};

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const ic = 'w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none';
</script>

<template>
  <form class="flex flex-col gap-4 p-6 max-h-[80vh] overflow-auto" @submit.prevent="handleSubmit">
    <div class="text-lg font-medium text-n-slate-12">
      {{ room.id ? $t('HOTEL_TARIFF.EDIT.TITLE') : $t('HOTEL_TARIFF.ADD.TITLE') }}
    </div>

    <!-- Basic info -->
    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_TARIFF.FORM.NAME.LABEL') }}</label>
      <input v-model="name" type="text" :class="ic" :placeholder="$t('HOTEL_TARIFF.FORM.NAME.PLACEHOLDER')" />
    </div>
    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_TARIFF.FORM.DESCRIPTION.LABEL') }}</label>
      <textarea v-model="description" rows="2" :class="ic" :placeholder="$t('HOTEL_TARIFF.FORM.DESCRIPTION.PLACEHOLDER')" />
    </div>
    <div>
      <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_TARIFF.FORM.AMENITIES.LABEL') }}</label>
      <textarea v-model="amenities" rows="2" :class="ic" :placeholder="$t('HOTEL_TARIFF.FORM.AMENITIES.PLACEHOLDER')" />
    </div>

    <div class="grid grid-cols-3 gap-4">
      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_TARIFF.FORM.TOTAL_UNITS.LABEL') }}</label>
        <input v-model.number="totalUnits" type="number" min="1" :class="ic" />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_TARIFF.FORM.MAX_GUESTS.LABEL') }}</label>
        <input v-model.number="maxGuests" type="number" min="1" :class="ic" />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_TARIFF.FORM.FREE_AGE_LIMIT.LABEL') }}</label>
        <input v-model.number="freeAgeLimit" type="number" min="0" :class="ic" />
        <span class="text-xs text-n-slate-10">{{ $t('HOTEL_TARIFF.FORM.FREE_AGE_LIMIT.HELP') }}</span>
      </div>
    </div>

    <!-- ADULTO -->
    <div class="border border-n-weak rounded-lg p-4">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Diárias Adulto</h3>
      <div class="mb-3">
        <span class="text-xs font-medium text-n-slate-11 mb-2 block">Normal</span>
        <div class="grid grid-cols-3 gap-3">
          <div><label class="block text-xs text-n-slate-10 mb-1">1 Diária</label><input v-model="price1Night" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">2 Diárias</label><input v-model="price2Nights" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">3 Diárias</label><input v-model="price3Nights" type="number" step="0.01" min="0" :class="ic" /></div>
        </div>
      </div>
      <div>
        <span class="text-xs font-medium text-n-amber-11 mb-2 block">Feriado</span>
        <div class="grid grid-cols-3 gap-3">
          <div><label class="block text-xs text-n-slate-10 mb-1">1 Diária</label><input v-model="holidayPrice1Night" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">2 Diárias</label><input v-model="holidayPrice2Nights" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">3 Diárias</label><input v-model="holidayPrice3Nights" type="number" step="0.01" min="0" :class="ic" /></div>
        </div>
      </div>
    </div>

    <!-- CRIANÇA FAIXA 1 -->
    <div class="border border-n-weak rounded-lg p-4">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Criança Faixa 1</h3>
      <div class="grid grid-cols-2 gap-3 mb-3">
        <div><label class="block text-xs text-n-slate-10 mb-1">Idade mínima</label><input v-model.number="child1MinAge" type="number" min="0" :class="ic" /></div>
        <div><label class="block text-xs text-n-slate-10 mb-1">Idade máxima</label><input v-model.number="child1MaxAge" type="number" min="0" :class="ic" /></div>
      </div>
      <div class="mb-3">
        <span class="text-xs font-medium text-n-slate-11 mb-2 block">Normal</span>
        <div class="grid grid-cols-3 gap-3">
          <div><label class="block text-xs text-n-slate-10 mb-1">1 Diária</label><input v-model="child1Price1" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">2 Diárias</label><input v-model="child1Price2" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">3 Diárias</label><input v-model="child1Price3" type="number" step="0.01" min="0" :class="ic" /></div>
        </div>
      </div>
      <div>
        <span class="text-xs font-medium text-n-amber-11 mb-2 block">Feriado</span>
        <div class="grid grid-cols-3 gap-3">
          <div><label class="block text-xs text-n-slate-10 mb-1">1 Diária</label><input v-model="holidayChild1Price1" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">2 Diárias</label><input v-model="holidayChild1Price2" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">3 Diárias</label><input v-model="holidayChild1Price3" type="number" step="0.01" min="0" :class="ic" /></div>
        </div>
      </div>
    </div>

    <!-- CRIANÇA FAIXA 2 -->
    <div class="border border-n-weak rounded-lg p-4">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Criança Faixa 2</h3>
      <div class="grid grid-cols-2 gap-3 mb-3">
        <div><label class="block text-xs text-n-slate-10 mb-1">Idade mínima</label><input v-model.number="child2MinAge" type="number" min="0" :class="ic" /></div>
        <div><label class="block text-xs text-n-slate-10 mb-1">Idade máxima</label><input v-model.number="child2MaxAge" type="number" min="0" :class="ic" /></div>
      </div>
      <div class="mb-3">
        <span class="text-xs font-medium text-n-slate-11 mb-2 block">Normal</span>
        <div class="grid grid-cols-3 gap-3">
          <div><label class="block text-xs text-n-slate-10 mb-1">1 Diária</label><input v-model="child2Price1" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">2 Diárias</label><input v-model="child2Price2" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">3 Diárias</label><input v-model="child2Price3" type="number" step="0.01" min="0" :class="ic" /></div>
        </div>
      </div>
      <div>
        <span class="text-xs font-medium text-n-amber-11 mb-2 block">Feriado</span>
        <div class="grid grid-cols-3 gap-3">
          <div><label class="block text-xs text-n-slate-10 mb-1">1 Diária</label><input v-model="holidayChild2Price1" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">2 Diárias</label><input v-model="holidayChild2Price2" type="number" step="0.01" min="0" :class="ic" /></div>
          <div><label class="block text-xs text-n-slate-10 mb-1">3 Diárias</label><input v-model="holidayChild2Price3" type="number" step="0.01" min="0" :class="ic" /></div>
        </div>
      </div>
    </div>

    <!-- CALENDAR -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <label class="text-sm font-medium text-n-slate-11">{{ $t('HOTEL_TARIFF.FORM.AVAILABLE_DATES.LABEL') }}</label>
        <span class="text-xs text-n-brand-11 font-medium">{{ totalSelectedDates }} dias no total</span>
      </div>
      <div class="border border-n-weak rounded-lg p-3 bg-n-surface-1">
        <div class="flex items-center justify-between mb-3">
          <button type="button" class="p-1 rounded hover:bg-n-slate-3 text-n-slate-11" @click="prevMonth">&larr;</button>
          <span class="text-sm font-medium text-n-slate-12 capitalize">{{ calendarTitle }}</span>
          <button type="button" class="p-1 rounded hover:bg-n-slate-3 text-n-slate-11" @click="nextMonth">&rarr;</button>
        </div>
        <div class="grid grid-cols-7 gap-1 mb-1">
          <div v-for="wd in weekDays" :key="wd" class="text-center text-xs font-medium text-n-slate-10 py-1">{{ wd }}</div>
        </div>
        <div class="grid grid-cols-7 gap-1">
          <template v-for="(day, idx) in calendarDays" :key="idx">
            <div v-if="!day" class="w-8 h-8" />
            <button
              v-else type="button"
              class="w-8 h-8 rounded text-xs font-medium transition-colors flex items-center justify-center"
              :class="{
                'bg-n-brand-9 text-white ring-2 ring-n-brand-7': day.selected && !day.past,
                'bg-n-brand-3 text-n-brand-11 opacity-60': day.selected && day.past,
                'hover:bg-n-slate-3 text-n-slate-12': !day.selected && !day.past,
                'text-n-slate-8 cursor-not-allowed': day.past && !day.selected,
              }"
              :disabled="day.past" @click="toggleDate(day.date)"
            >{{ day.day }}</button>
          </template>
        </div>
        <div class="flex items-center justify-between mt-3 pt-2 border-t border-n-weak">
          <span class="text-xs text-n-slate-10">{{ selectedCountThisMonth }} dias neste mês</span>
          <div class="flex gap-2">
            <button type="button" class="text-xs text-n-brand-11 hover:underline" @click="selectAllMonth">Selecionar todos</button>
            <button type="button" class="text-xs text-n-ruby-11 hover:underline" @click="clearAllMonth">Limpar todos</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Active -->
    <div class="flex items-center gap-2">
      <input id="room-active" v-model="active" type="checkbox" class="rounded border-n-weak text-n-brand-9 focus:ring-n-brand-9" />
      <label for="room-active" class="text-sm text-n-slate-11">{{ $t('HOTEL_TARIFF.FORM.ACTIVE.LABEL') }}</label>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-2 pt-2">
      <Button :label="$t('HOTEL_TARIFF.ADD.CANCEL_BUTTON_TEXT')" slate sm @click="emit('close')" />
      <Button :label="$t('HOTEL_TARIFF.FORM.SUBMIT')" sm :disabled="!isFormValid || isSubmitting" :is-loading="isSubmitting" @click="handleSubmit" />
    </div>
  </form>
</template>
